/**
 * Shared texting for admins (1.12.32, Teampace 2959 / 2960 / 2966): where a number's texts go, in
 * words, and the checks and words for a ring group's or queue's texting rules. Pure, so it's tested
 * without a browser (apps/web/test/texting-admin.test.ts).
 */
import type { TeamKind, TeamRules, TextRouteRow, TextRoutesPage } from '../api/client';

/** "Support (queue)", "Front Office (ring group)". */
export function teamLabel(kind: string, name: string): string {
  return kind === 'ringGroup' ? `${name} (ring group)` : kind === 'callQueue' ? `${name} (queue)` : name;
}

/**
 * Someone in a "Hand to…" list, in the same words in Messages and on the wallboard (3137):
 * "Supervisor · In Stitch now", "Signed out of Billing · Not in Stitch now". A supervisor can take
 * the team's texts signed in or not, so it isn't said for them.
 */
export function handToWords(p: { here: boolean; signedIn: boolean; supervisor: boolean }, team: string): string {
  return [p.supervisor ? 'Supervisor' : '', !p.signedIn && !p.supervisor ? `Signed out of ${team}` : '', p.here ? 'In Stitch now' : 'Not in Stitch now'].filter(Boolean).join(' · ');
}

/** "ring group", "call queue": as people say them. */
export const TEAM_WORD: Record<TeamKind, string> = { ringGroup: 'ring group', callQueue: 'call queue' };

export interface TextsGoTo {
  /** "Same as calls · Dana Ruiz", "Support (queue)", "Admins", "Nobody (texting off)". */
  text: string;
  /** Why it's the admins, in a few words. */
  note: string | null;
  tone: 'normal' | 'admins' | 'off';
}

/** Where a number's texts go now, as the Numbers page shows it. */
export function textsGoTo(row: TextRouteRow, teams: TextRoutesPage['teams']): TextsGoTo {
  if (row.reach === 'off') return { text: 'Nobody (texting off)', note: null, tone: 'off' };
  const same = !row.texts.set;
  const kind = row.texts.reachKind;
  const name = row.texts.name;
  if (row.reach === 'admins') {
    const team = kind === 'ringGroup' || kind === 'callQueue' ? teams.find((t) => t.extension === (same ? row.calls.to : row.texts.target)) : undefined;
    const why =
      kind === 'ringGroup' || kind === 'callQueue'
        ? team && !team.takesTexts
          ? `${name} doesn’t take texts`
          : `Nobody in ${name} uses Stitch`
        : kind === 'autoAttendant'
          ? `${name} is a menu`
          : kind === 'none'
            ? 'its calls don’t go anywhere yet'
            : 'it rings nobody who uses Stitch';
    return { text: 'Admins', note: same ? `Same as calls: ${why}` : why, tone: 'admins' };
  }
  const where = teamLabel(kind, name);
  return { text: same ? `Same as calls · ${where}` : where, note: null, tone: 'normal' };
}

/** "1 number’s texts go only to your admins", "3 numbers’ texts …". */
export function adminsNotice(n: number): string {
  return n === 1 ? '1 number’s texts go only to your admins' : `${n} numbers’ texts go only to your admins`;
}

/**
 * Same as calls, in the Texts go to panel: where the calls go, and so who would get the texts. A
 * ring group or queue counts its people in Stitch (TextRoutesPage.teams) and whether it takes texts.
 */
export function sameAsCalls(row: TextRouteRow, page: Pick<TextRoutesPage, 'teams' | 'people'>): string {
  const c = row.calls;
  const admins = 'so your admins would get them';
  switch (c.kind) {
    case 'person':
      return page.people.some((p) => p.extension === c.to) ? `Calls go to ${c.name}, so texts do too.` : `Calls go to ${c.name}, who doesn’t use Stitch, ${admins}.`;
    case 'ringGroup':
    case 'callQueue': {
      const t = page.teams.find((x) => x.extension === c.to);
      if (t && !t.takesTexts) return `Calls go to ${teamLabel(c.kind, c.name)}, which doesn’t take texts, ${admins}.`;
      if (t && t.people === 0) return `Calls go to ${teamLabel(c.kind, c.name)}, where nobody uses Stitch, ${admins}.`;
      return `Calls go to ${teamLabel(c.kind, c.name)}, and its people share the texts.`;
    }
    case 'autoAttendant':
      return `Calls go to ${c.name}, a menu, ${admins}.`;
    case 'none':
      return `Calls don’t go anywhere yet, ${admins}.`;
    default:
      return `Calls go to ${c.name}, which nobody in Stitch answers, ${admins}.`;
  }
}

/** Whose texts a number brings to a person (their page): theirs alone, and whether it rings them too. */
export function personNumbers(rows: readonly TextRouteRow[], extension: string): { row: TextRouteRow; use: 'Calls and texts' | 'Texts only' }[] {
  return rows
    .filter((r) => r.canText && r.reach === 'person')
    .filter((r) => (r.texts.set ? r.texts.kind === 'person' && r.texts.target === extension : r.calls.kind === 'person' && r.calls.to === extension))
    .map((row) => ({ row, use: row.calls.to === extension ? 'Calls and texts' : 'Texts only' }));
}

// --- a team's texting rules ------------------------------------------------------------------------

/** An automatic text with its blanks filled in, as Stitch sends it: {company}, {team}, {place}, {wait}. */
export function fillAutoText(template: string, vars: { company: string; team: string; place?: number; wait?: string }): string {
  return template
    .replace(/\{company\}/g, vars.company)
    .replace(/\{team\}/g, vars.team)
    .replace(/\{place\}/g, vars.place !== undefined ? String(vars.place) : '1')
    .replace(/\{wait\}/g, vars.wait ?? 'a few minutes')
    .replace(/\s+/g, ' ')
    .trim();
}

/** The settings typed as numbers, with what the server takes. */
export const RULE_NUMBERS = {
  limit: { min: 1, max: 50, label: 'Open conversations per person', unit: '' },
  replyMinutes: { min: 1, max: 240, label: 'Reply time', unit: 'minutes' },
  supervisorMinutes: { min: 1, max: 1440, label: 'Telling the supervisors', unit: 'minutes' },
  returningDays: { min: 0, max: 30, label: 'Returning customers', unit: 'days (0: never)' },
  awayMinutes: { min: 1, max: 120, label: 'Away', unit: 'minutes' },
  signedOutMinutes: { min: 0, max: 60, label: 'Signed out', unit: 'minutes (0: at once)' },
  'place.firstMinutes': { min: 1, max: 60, label: 'The first place-in-line text', unit: 'minutes' },
  'place.updateMinutes': { min: 2, max: 240, label: 'The update', unit: 'minutes' }
} as const;
export type RuleNumber = keyof typeof RULE_NUMBERS;

const TEXTS: Record<string, string> = { 'place.text': 'The place-in-line text', 'afterHours.text': 'The after-hours reply', 'opening.text': 'The opening text' };

function rangeWords(key: RuleNumber): string {
  const r = RULE_NUMBERS[key];
  return `${r.label}: a whole number from ${r.min} to ${r.max}${r.unit ? ` ${r.unit}` : ''}.`;
}

/** The numbers as typed, from rules. */
export function ruleInputs(r: TeamRules): Record<RuleNumber, string> {
  return {
    limit: String(r.limit),
    replyMinutes: String(r.replyMinutes),
    supervisorMinutes: String(r.supervisorMinutes),
    returningDays: String(r.returningDays),
    awayMinutes: String(r.awayMinutes),
    signedOutMinutes: String(r.signedOutMinutes),
    'place.firstMinutes': String(r.place.firstMinutes),
    'place.updateMinutes': String(r.place.updateMinutes)
  };
}

/**
 * Rules ready to save, from the form: the numbers as typed, checked as the server checks them, and
 * the texts (each up to `maxText`, and written when it's on). Errors are keyed by setting.
 */
export function rulesToSave(base: TeamRules, inputs: Record<RuleNumber, string>, maxText: number): { rules: TeamRules | null; errors: Partial<Record<RuleNumber | 'place.text' | 'afterHours.text' | 'opening.text', string>> } {
  const errors: Partial<Record<RuleNumber | 'place.text' | 'afterHours.text' | 'opening.text', string>> = {};
  const n = {} as Record<RuleNumber, number>;
  for (const key of Object.keys(RULE_NUMBERS) as RuleNumber[]) {
    const raw = inputs[key].trim();
    const v = /^\d{1,5}$/.test(raw) ? Number(raw) : NaN;
    if (!Number.isInteger(v) || v < RULE_NUMBERS[key].min || v > RULE_NUMBERS[key].max) errors[key] = rangeWords(key);
    n[key] = v;
  }
  if (!errors['place.firstMinutes'] && !errors['place.updateMinutes'] && n['place.updateMinutes'] <= n['place.firstMinutes']) {
    errors['place.updateMinutes'] = `The update comes after the first text: more than ${n['place.firstMinutes']} minutes.`;
  }
  for (const [key, t] of [
    ['place.text', base.place],
    ['afterHours.text', base.afterHours],
    ['opening.text', base.opening]
  ] as const) {
    if (t.text.trim().length > maxText) errors[key] = `${TEXTS[key]} is too long: up to ${maxText} characters.`;
    else if (t.on && !t.text.trim()) errors[key] = `Write ${TEXTS[key]!.toLowerCase()}, or turn it off.`;
  }
  if (Object.keys(errors).length) return { rules: null, errors };
  return {
    rules: {
      ...base,
      limit: n.limit,
      replyMinutes: n.replyMinutes,
      supervisorMinutes: n.supervisorMinutes,
      returningDays: n.returningDays,
      awayMinutes: n.awayMinutes,
      signedOutMinutes: n.signedOutMinutes,
      place: { ...base.place, firstMinutes: n['place.firstMinutes'], updateMinutes: n['place.updateMinutes'], text: base.place.text.trim() },
      afterHours: { ...base.afterHours, text: base.afterHours.text.trim() },
      opening: { ...base.opening, text: base.opening.text.trim() }
    },
    errors
  };
}

/**
 * The server's answer to rules it wouldn't take, in plain words. Its own refusals are written for
 * people already; a form check ("place.text: String must contain at most 400 character(s)") is
 * turned into what the setting is called and what it takes.
 */
export function plainRulesError(err: unknown): string {
  const e = err as { code?: unknown; message?: unknown } | null;
  const message = typeof e?.message === 'string' ? e.message : '';
  if (e?.code !== 'BAD_REQUEST') return message || 'That didn’t save. Try again.';
  const said = message
    .split(';')
    .map((part) => part.split(':')[0]!.trim())
    .map((path) => (path in RULE_NUMBERS ? rangeWords(path as RuleNumber) : TEXTS[path] ? `${TEXTS[path]} is too long.` : path === 'supervisors' || path.startsWith('supervisors.') ? 'Choose supervisors from your company’s people.' : null))
    .filter((x): x is string => Boolean(x));
  return said.length ? [...new Set(said)].join(' ') : 'Something in these rules isn’t right. Check the numbers and texts, then save again.';
}

/** What each texting rule is called, for Activity ("Changed Support’s texting rules: reply time, supervisors"). */
export const RULE_WORDS: Record<keyof TeamRules, string> = {
  takesTexts: 'takes texts',
  handout: 'handing out',
  limit: 'open conversations per person',
  replyMinutes: 'reply time',
  supervisorMinutes: 'when supervisors are told',
  onCall: 'people on a call',
  returningDays: 'returning customers',
  awayMinutes: 'away time',
  signedOutMinutes: 'signed-out time',
  ownerOnly: 'who replies',
  hours: 'open hours',
  place: 'place-in-line texts',
  afterHours: 'after-hours reply',
  opening: 'opening text',
  supervisors: 'supervisors',
  summaries: 'Stitch AI summary',
  urgent: 'urgent words'
};
